import { addressTypeLabel } from '../utils/labTestUtils';
import { displayPatientCode } from '../utils/patientCode';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { patientAPI, addressAPI } from '../services/api';

import Icon from '../components/Icon';
const EMPTY_ADDR = {
  addressType: 'HOME', street: '', city: '',
  state: 'Karnataka', zipCode: '', country: 'India',
  landmark: '', isDefault: false,
};

export default function AddressPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [patient,    setPatient]   = useState(null);
  const [addresses,  setAddresses] = useState([]);
  const [form,       setForm]      = useState(EMPTY_ADDR);
  const [editId,     setEditId]    = useState(null); // null = add mode, uuid = edit mode
  const [saving,     setSaving]    = useState(false);
  const [error,      setError]     = useState('');
  const [success,    setSuccess]   = useState('');

  const load = () => {
    patientAPI.get(id).then(r => setPatient(r.data)).catch(() => navigate('/patients'));
    addressAPI.listByPatient(id)
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        setAddresses(list);
        // Auto-populate edit form if ?edit=uuid is in URL
        const editAddrId = searchParams.get('edit');
        if (editAddrId) {
          const addr = list.find(a => a.id === editAddrId);
          if (addr) {
            setEditId(addr.id);
            setForm({
              addressType: addr.addressType || 'HOME',
              street:      addr.street || '',
              city:        addr.city || '',
              state:       addr.state || 'Karnataka',
              zipCode:     addr.zipCode || '',
              country:     addr.country || 'India',
              landmark:    addr.landmark || '',
              isDefault:   addr.isDefault || false,
            });
            setTimeout(() => document.getElementById('addr-form')?.scrollIntoView({ behavior: 'smooth' }), 200);
          }
        }
      })
      .catch(() => {});
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const set = (k) => (e) => setForm(f => ({
    ...f,
    [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  // Populate form with existing address for editing
  const handleEdit = (addr) => {
    setEditId(addr.id);
    setForm({
      addressType: addr.addressType || 'HOME',
      street:      addr.street || '',
      city:        addr.city || '',
      state:       addr.state || 'Karnataka',
      zipCode:     addr.zipCode || '',
      country:     addr.country || 'India',
      landmark:    addr.landmark || '',
      isDefault:   addr.isDefault || false,
    });
    setError('');
    setSuccess('');
    // Scroll to form
    setTimeout(() => document.getElementById('addr-form')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setForm(EMPTY_ADDR);
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      if (editId) {
        await addressAPI.update(id, editId, form);
        setSuccess('Address updated successfully!');
      } else {
        await addressAPI.create(id, form);
        setSuccess('Address saved successfully!');
      }
      setForm(EMPTY_ADDR);
      setEditId(null);
      if (editId) {
        navigate(`/patients/${id}?tab=Addresses`);
      } else {
        load();
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors
        ? JSON.stringify(err.response?.data?.errors)
        : 'Failed to save address.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (addrId) => {
    if (!window.confirm('Remove this address?')) return;
    try {
      await addressAPI.delete(id, addrId);
      setAddresses(a => a.filter(addr => addr.id !== addrId));
      if (editId === addrId) handleCancelEdit();
    } catch { setError('Failed to delete address.'); }
  };

  if (!patient) return null;

  const fullName = `${patient.firstName} ${patient.lastName}`;

  return (
    <AppShell breadcrumbs={[
      { label: 'Patients', to: '/patients' },
      { label: fullName, to: `/patients/${id}` },
      { label: 'Addresses' },
    ]}>
      <span className="back-link fade-in-up" onClick={() => navigate(`/patients/${id}`)}>
        ← Back to Patient
      </span>

      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">Address Management</div>
          <div className="page-subtitle">
            {[fullName, displayPatientCode(patient.patientCode)].filter(Boolean).join(' — ')}
          </div>
        </div>
      </div>

      {error   && <div className="alert alert-error"><Icon name="alert" size={14} /> {error}</div>}
      {success && <div className="alert alert-success">✓ {success}</div>}

      {/* Existing addresses */}
      {addresses.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 22 }}
          className="fade-in-up delay-1">
          {addresses.map(addr => (
            <div
              className={`address-card ${addr.isDefault ? 'primary' : ''} ${editId === addr.id ? 'editing' : ''}`}
              key={addr.id}
              style={{ border: editId === addr.id ? '2px solid var(--brand-accent)' : undefined }}
            >
              {addr.isDefault && (
                <div style={{ position: 'absolute', top: 14, right: 14 }}>
                  <span className="badge badge-confirmed">Default</span>
                </div>
              )}
              {editId === addr.id && (
                <div style={{ position: 'absolute', top: 14, right: addr.isDefault ? 90 : 14 }}>
                  <span className="badge" style={{ background: 'var(--amber-bg)', color: 'var(--amber)' }}>
                    Editing
                  </span>
                </div>
              )}
              {/* DEF-039 — same chip as AddressSlotStep; see addressTypeLabel().
                  paddingRight keeps it clear of the absolutely positioned
                  Default/Editing badges above rather than clipping under them. */}
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)',
                letterSpacing: '0.6px', marginBottom: 10,
                paddingRight: (addr.isDefault || editId === addr.id) ? 76 : 0,
                whiteSpace: 'nowrap' }}>
                {addressTypeLabel(addr.addressType)}
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.8, color: 'var(--text-primary)' }}>
                <strong>{addr.street}</strong><br />
                {addr.landmark && <>{addr.landmark}<br /></>}
                {addr.city}, {addr.state} – {addr.zipCode}<br />
                <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{addr.country}</span>
              </div>
              <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {editId === addr.id ? (
                  <button className="btn btn-ghost btn-sm" onClick={handleCancelEdit}>
                    ✕ Cancel Edit
                  </button>
                ) : (
                  <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(addr)}>
                    ✏ Edit
                  </button>
                )}
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(addr.id)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit form */}
      <div className="form-section fade-in-up delay-2" id="addr-form">
        <div className="form-section-title">
          {editId ? 'Edit Address' : 'Add New Address'}
        </div>
        <form onSubmit={handleSave}>
          <div className="form-grid three-col">
            <div className="form-group">
              <label className="form-label">Address Type</label>
              <select className="form-select" value={form.addressType} onChange={set('addressType')}>
                <option value="HOME">Home</option>
                <option value="OFFICE">Office</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: '2/4' }}>
              <label className="form-label">Street / Building <span className="required">*</span></label>
              <input className="form-input" value={form.street} onChange={set('street')}
                placeholder="Flat no., Building, Street" required />
            </div>
            <div className="form-group">
              <label className="form-label">City <span className="required">*</span></label>
              <input className="form-input" value={form.city} onChange={set('city')}
                placeholder="City" required />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <select className="form-select" value={form.state} onChange={set('state')}>
                {['Karnataka','Tamil Nadu','Maharashtra','Telangana','Kerala',
                  'Andhra Pradesh','Delhi','Gujarat','Rajasthan','Other']
                  .map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">PIN Code <span className="required">*</span></label>
              <input className="form-input" value={form.zipCode} onChange={set('zipCode')}
                placeholder="6-digit PIN" maxLength={6} required />
            </div>
            <div className="form-group">
              <label className="form-label">Country <span className="required">*</span></label>
              <input className="form-input" value={form.country} onChange={set('country')}
                placeholder="Country" required />
            </div>
            <div className="form-group">
              <label className="form-label">Landmark</label>
              <input className="form-input" value={form.landmark} onChange={set('landmark')}
                placeholder="Nearby landmark (optional)" />
            </div>
            <div className="form-group form-full"
              style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" id="isDefault" checked={form.isDefault}
                onChange={set('isDefault')} />
              <label htmlFor="isDefault" style={{ fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                Set as default address
              </label>
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : editId ? '✓ Update Address' : '✓ Save Address'}
            </button>
            {editId && (
              <button className="btn btn-ghost" type="button" onClick={handleCancelEdit}>
                Cancel
              </button>
            )}
            <button className="btn btn-ghost" type="button"
              onClick={() => navigate(`/patients/${id}`)}>
              Done
            </button>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
